import { useEffect, useState } from 'react';
import { Logo } from '../Logo';

type Asset = { id: string; filename: string | null; contentType: string | null; bytes: number | null; at: string };
type Payload = {
  event: { name: string; date: string | null; slug: string };
  total: number;
  uploaders: number;
  assets: Asset[];
};

export function HostGallery({ slug }: { slug: string }) {
  const [data, setData] = useState<Payload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const url = new URL(window.location.href);
      const key = url.searchParams.get('k');

      // Trade the key for an HttpOnly cookie, then take it out of the address
      // bar so it stays out of history, screenshots and shoulder-surfing.
      if (key) {
        const res = await fetch(`/api/e/${slug}/session`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ hostKey: key }),
        });
        url.searchParams.delete('k');
        window.history.replaceState(null, '', url.toString());
        if (!res.ok) { setError('That key is not right for this event.'); return; }
      }

      const res = await fetch(`/api/e/${slug}/assets`);
      if (res.status === 401) { setError('This gallery is private. Open it with your host link.'); return; }
      if (!res.ok) { setError('Could not load the gallery.'); return; }
      setData(await res.json());
    })().catch(() => setError('Could not load the gallery.'));
  }, [slug]);

  if (error) {
    return (
      <div className="page guest">
        <div className="shell guest-inner">
          <Logo size={30} />
          <h1>Private gallery</h1>
          <p className="lede">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page guest">
        <div className="shell guest-inner">
          <Logo size={30} />
          <p className="note">Loading&hellip;</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="shell gallery-head">
        <Logo size={26} />
        <h1>{data.event.name}</h1>
        <p className="note">
          {data.total} {data.total === 1 ? 'memory' : 'memories'} &middot; {data.uploaders}{' '}
          {data.uploaders === 1 ? 'guest' : 'guests'}
          {data.event.date ? ` · ${data.event.date}` : ''}
        </p>
      </div>

      {data.assets.length === 0 ? (
        <div className="shell guest-inner">
          <p className="lede">Nothing yet. The first photo will appear here the moment someone adds it.</p>
        </div>
      ) : (
        <div className="shell grid">
          {data.assets.map((a) => {
            const isVideo = (a.contentType ?? '').startsWith('video/');
            const src = `/api/e/${slug}/asset/${a.id}`;
            return (
              <figure key={a.id} className="tile">
                {isVideo ? (
                  <video src={src} controls preload="metadata" playsInline />
                ) : (
                  <img src={src} alt={a.filename ?? 'Guest photo'} loading="lazy" decoding="async" />
                )}
              </figure>
            );
          })}
        </div>
      )}
    </div>
  );
}
